import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import Icon from './Icon';
import Recorder from './Recorder';
import PhoneRecord from './PhoneRecord';
import { useIntegrations } from '../lib/IntegrationContext';

// ReportIssue — global "Report an issue" feature.
//
// Mounted once at app root (App.js handles importing/mounting). Renders a
// fixed floating action button (bottom-right) and a modal wizard that records
// a screen+voice repro, runs AI extraction, lets the user review/edit an
// auto-drafted ticket, and files it to Jira.
//
// Opens via the FAB or the keyboard shortcut Cmd/Ctrl+Shift+R.

const ISSUE_TYPES = ['Bug', 'Task', 'Story'];

// Blank review form — used when the user skips recording to write it manually.
const EMPTY_FORM = {
  summary: '',
  area: '',
  steps_to_reproduce: '',
  expected: '',
  actual: '',
};

export default function ReportIssue() {
  const { status } = useIntegrations();
  const jiraConnected = !!status?.jira;

  const [open, setOpen] = useState(false);

  // Wizard step: 'record' | 'extracting' | 'review' | 'done'
  const [step, setStep] = useState('record');

  // Route captured once when the modal opens, passed to extraction + ticket.
  const [route, setRoute] = useState('');

  // Recording mode: 'device' | 'phone'
  const [recordMode, setRecordMode] = useState('device');

  // Recording / extraction
  const [videoPath, setVideoPath] = useState('');
  const [extractError, setExtractError] = useState('');

  // Review form
  const [form, setForm] = useState(EMPTY_FORM);

  // Projects
  const [projects, setProjects] = useState([]);
  const [projectKey, setProjectKey] = useState('');
  const [projectsError, setProjectsError] = useState('');
  const [issueType, setIssueType] = useState('Bug');

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [created, setCreated] = useState(null); // { key, url }

  // ── Reset all wizard state to its initial values ──
  const resetWizard = useCallback(() => {
    setStep('record');
    setRecordMode('device');
    setRoute('');
    setVideoPath('');
    setExtractError('');
    setForm(EMPTY_FORM);
    setProjects([]);
    setProjectKey('');
    setProjectsError('');
    setIssueType('Bug');
    setSubmitting(false);
    setSubmitError('');
    setCreated(null);
  }, []);

  // ── Open / close ──
  const handleOpen = useCallback(() => {
    resetWizard();
    // Capture the route the user is currently on, once, at open.
    setRoute(window.location?.pathname || '');
    setOpen(true);
  }, [resetWizard]);

  const handleClose = useCallback(() => {
    setOpen(false);
    resetWizard();
  }, [resetWizard]);

  // ── Keyboard shortcut: Cmd/Ctrl+Shift+R ──
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'R' || e.key === 'r')) {
        e.preventDefault();
        // Toggle: if already open, leave it; otherwise open.
        if (!open) handleOpen();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, handleOpen]);

  // ── Fetch Jira projects whenever we land on the review step (and don't
  //    already have them). Defaults the selected project to the API default. ──
  useEffect(() => {
    if (!open || step !== 'review' || projects.length > 0) return;
    let cancelled = false;
    setProjectsError('');
    api.get('/jira/projects')
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data?.projects) ? r.data.projects : [];
        setProjects(list);
        const def = r.data?.default;
        setProjectKey((prev) => prev || def || list[0] || '');
      })
      .catch(() => {
        if (!cancelled) setProjectsError('Could not load Jira projects.');
      });
    return () => { cancelled = true; };
  }, [open, step, projects.length]);

  // ── Recorder callback → kick off extraction ──
  const handleRecorded = useCallback((savedPath) => {
    if (!savedPath) {
      setExtractError('No recording was captured. Please try again.');
      return;
    }
    setVideoPath(savedPath);
    setExtractError('');
    setStep('extracting');

    api.post('/issues/extract', { video_path: savedPath, route })
      .then((r) => {
        const d = r.data || {};
        setForm({
          summary: d.summary || '',
          area: d.area || '',
          steps_to_reproduce: Array.isArray(d.steps_to_reproduce)
            ? d.steps_to_reproduce.join('\n')
            : (d.steps_to_reproduce || ''),
          expected: d.expected || '',
          actual: d.actual || '',
        });
        setStep('review');
      })
      .catch(() => {
        setExtractError('We could not analyze your recording. You can try again.');
      });
  }, [route]);

  // ── Skip recording → empty review form, no video_path ──
  const handleSkipRecording = useCallback(() => {
    setVideoPath('');
    setForm(EMPTY_FORM);
    setExtractError('');
    setStep('review');
  }, []);

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // ── Submit → compose description + create the ticket ──
  const handleSubmit = useCallback(() => {
    if (!form.summary?.trim()) {
      setSubmitError('A summary is required.');
      return;
    }
    if (!projectKey) {
      setSubmitError('Please choose a Jira project.');
      return;
    }

    const steps = (form.steps_to_reproduce || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    const stepsBlock = steps.length
      ? steps.map((s, i) => `${i + 1}. ${s}`).join('\n')
      : '(none provided)';

    const description =
      `Area: ${form.area || '(unspecified)'}\n\n` +
      `Steps to reproduce:\n${stepsBlock}\n\n` +
      `Expected: ${form.expected || ''}\n\n` +
      `Actual: ${form.actual || ''}`;

    setSubmitting(true);
    setSubmitError('');

    api.post('/jira/tickets/create', {
      summary: form.summary.trim(),
      description,
      project_key: projectKey,
      issue_type: issueType,
      origin: 'report',
    })
      .then((r) => {
        const d = r.data || {};
        if (d.ok === false) {
          setSubmitError('Jira rejected the ticket. Please check the fields and try again.');
          return;
        }
        setCreated({ key: d.key, url: d.url });
        setStep('done');
      })
      .catch(() => {
        setSubmitError('Could not create the Jira ticket. Please try again.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [form, projectKey, issueType]);

  // ── Report another → reset back to the record step, keep modal open ──
  const handleReportAnother = useCallback(() => {
    resetWizard();
    setRoute(window.location?.pathname || '');
  }, [resetWizard]);

  return (
    <>
      {/* ── Floating action button ── */}
      <button
        type="button"
        className="report-fab"
        onClick={handleOpen}
        title="Report an issue (⌘/Ctrl+Shift+R)"
        aria-label="Report an issue"
      >
        <Icon name="warning" size={18} />
        <span className="report-fab-label" aria-hidden="true">Report issue</span>
      </button>

      {/* ── Modal wizard ── */}
      {open && (
        <div
          className="jira-drawer-overlay"
          onClick={handleClose}
          style={{ zIndex: 950, justifyContent: 'center', alignItems: 'flex-start' }}
        >
          <div
            className="jira-drawer"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: '92vw', height: 'auto', maxHeight: '92vh', marginTop: '4vh', borderRadius: 12 }}
          >
            <div className="jira-drawer-header">
              <div>
                <div className="jira-drawer-key">Report an issue</div>
                <div className="jira-drawer-summary">
                  {!jiraConnected
                    ? 'Jira not connected'
                    : step === 'record'
                      ? 'Record the problem'
                      : step === 'extracting'
                        ? 'Analyzing…'
                        : step === 'review'
                          ? 'Review & send to Build'
                          : 'Done'}
                </div>
              </div>
              <button className="jira-drawer-close" onClick={handleClose}>✕</button>
            </div>

            {/* ── Gate: Jira must be connected ── */}
            {!jiraConnected && (
              <div className="jira-drawer-section">
                <p className="jira-drawer-desc" style={{ marginBottom: 16 }}>
                  Connect Jira to enable issue reporting.
                </p>
                <a href="/settings" className="btn btn-primary">
                  Go to Settings
                </a>
              </div>
            )}

            {/* ── Step: record ── */}
            {jiraConnected && step === 'record' && (
              <div className="jira-drawer-section">
                <p className="jira-drawer-desc" style={{ marginBottom: 14 }}>
                  Record what went wrong — narrate the problem as you reproduce it.
                  We'll turn it into a Jira ticket.
                </p>

                {/* Device / Phone toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[
                    { id: 'device', label: '🖥 This device' },
                    { id: 'phone',  label: '📱 Phone (QR)' },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setRecordMode(id)}
                      style={{
                        padding: '6px 14px', borderRadius: 999, fontSize: 13,
                        fontWeight: 500, cursor: 'pointer', border: '1px solid',
                        borderColor: recordMode === id ? 'var(--gp-accent)' : 'var(--gp-border)',
                        background: recordMode === id ? 'var(--gp-primary-fill)' : 'transparent',
                        color: recordMode === id ? '#fff' : 'var(--gp-ink)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {recordMode === 'device' && (
                  <Recorder onRecordingReady={handleRecorded} />
                )}
                {recordMode === 'phone' && (
                  <PhoneRecord onVideoReady={(videoPath) => handleRecorded(videoPath)} />
                )}

                <div style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleSkipRecording}
                  >
                    Skip recording, write it myself →
                  </button>
                </div>
              </div>
            )}

            {/* ── Step: extracting ── */}
            {jiraConnected && step === 'extracting' && (
              <div className="jira-drawer-section">
                {!extractError ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '24px 0', color: 'var(--gp-muted)' }}>
                    <span className="spinner" />
                    <span>Analyzing your recording…</span>
                  </div>
                ) : (
                  <>
                    <div className="jira-sync-banner jira-sync-banner--err" style={{ marginBottom: 16 }}>
                      {extractError}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setExtractError(''); setStep('record'); }}
                    >
                      Back to recording
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ── Step: review ── */}
            {jiraConnected && step === 'review' && (
              <div className="jira-drawer-section">
                <div className="form-group">
                  <label className="label" htmlFor="ri-summary">Summary *</label>
                  <input
                    id="ri-summary"
                    className="input"
                    type="text"
                    value={form.summary}
                    onChange={setField('summary')}
                    placeholder="Short description of the problem"
                  />
                </div>

                <div className="form-group">
                  <label className="label" htmlFor="ri-area">Area</label>
                  <input
                    id="ri-area"
                    className="input"
                    type="text"
                    value={form.area}
                    onChange={setField('area')}
                    placeholder="e.g. Editor, Billing, Onboarding"
                  />
                </div>

                <div className="form-group">
                  <label className="label" htmlFor="ri-steps">Steps to reproduce (one per line)</label>
                  <textarea
                    id="ri-steps"
                    className="textarea"
                    value={form.steps_to_reproduce}
                    onChange={setField('steps_to_reproduce')}
                    placeholder={'Open the editor\nClick Save\n…'}
                    style={{ minHeight: 110 }}
                  />
                </div>

                <div className="form-group">
                  <label className="label" htmlFor="ri-expected">Expected</label>
                  <textarea
                    id="ri-expected"
                    className="textarea"
                    value={form.expected}
                    onChange={setField('expected')}
                    placeholder="What should have happened"
                    style={{ minHeight: 60 }}
                  />
                </div>

                <div className="form-group">
                  <label className="label" htmlFor="ri-actual">Actual</label>
                  <textarea
                    id="ri-actual"
                    className="textarea"
                    value={form.actual}
                    onChange={setField('actual')}
                    placeholder="What actually happened"
                    style={{ minHeight: 60 }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 16 }}>
                    <label className="label" htmlFor="ri-project">Jira project</label>
                    <select
                      id="ri-project"
                      className="select"
                      value={projectKey}
                      onChange={(e) => setProjectKey(e.target.value)}
                    >
                      {projects.length === 0 && <option value="">No projects available</option>}
                      {projects.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" style={{ flex: 1, minWidth: 160, marginBottom: 16 }}>
                    <label className="label" htmlFor="ri-type">Issue type</label>
                    <select
                      id="ri-type"
                      className="select"
                      value={issueType}
                      onChange={(e) => setIssueType(e.target.value)}
                    >
                      {ISSUE_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {projectsError && (
                  <div className="jira-sync-banner jira-sync-banner--warn" style={{ marginBottom: 12 }}>
                    {projectsError}
                  </div>
                )}

                {submitError && (
                  <div className="jira-sync-banner jira-sync-banner--err" style={{ marginBottom: 12 }}>
                    {submitError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting && <span className="spinner" style={{ width: 14, height: 14 }} />}
                    {submitting ? 'Creating…' : 'Send to Build →'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── Step: done ── */}
            {jiraConnected && step === 'done' && (
              <div className="jira-drawer-section">
                <div className="jira-sync-banner jira-sync-banner--ok" style={{ marginBottom: 16 }}>
                  Sent to Build → {created?.key || 'ticket'}
                  {created?.url && (
                    <a
                      href={created.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost btn-sm"
                      style={{ marginLeft: 12 }}
                    >
                      Open in Jira →
                    </a>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn btn-secondary" onClick={handleReportAnother}>
                    Report another
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleClose}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
